import React, { useState } from "react";
import { categories } from "@/lib/constants";
import {
  Combobox,
  Portal,
  useFilter,
  useListCollection,
  Badge,
  Wrap,
} from "@chakra-ui/react";

interface CategoryComboboxProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  width?: string | number;
  placeholder?: string;
  multiple?: boolean;
}

export const CategoryCombobox: React.FC<CategoryComboboxProps> = ({
  value,
  onChange,
  width = "100%",
  placeholder = "Type to search category",
  multiple = true,
}) => {
  // Build grouped items for the combobox
  const groupedCategoryItems = categories.flatMap((cat) => [
    { label: cat.label, value: `header-${cat.value}`, isHeader: true },
    ...cat.subcategories.map((sub) => ({ ...sub, isHeader: false })),
  ]);

  const { contains } = useFilter({ sensitivity: "base" });
  const { collection, filter } = useListCollection({
    initialItems: groupedCategoryItems,
    filter: contains,
    itemToValue: (item) => item.value,
    itemToString: (item) => item.label,
    isItemDisabled: (item) => !!item.isHeader,
  });

  const [inputValue, setInputValue] = useState("");

  // For single select, find the selected label
  let selectedLabel = "";
  if (!multiple && typeof value === "string" && value) {
    const item = categories
      .flatMap((cat) => cat.subcategories)
      .find((i) => i.value === value);
    selectedLabel = item ? item.label : value;
  }

  return (
    <Combobox.Root
      multiple={multiple}
      closeOnSelect
      collection={collection}
      onInputValueChange={(e) => {
        filter(e.inputValue);
        setInputValue(e.inputValue);
      }}
      onValueChange={(details) =>
        onChange(
          multiple ? (details.value as string[]) : details.value[0] || "",
        )
      }
      value={
        multiple
          ? Array.isArray(value)
            ? value
            : []
          : typeof value === "string"
            ? [value]
            : []
      }
      width={width}
    >
      <Combobox.Control>
        <Combobox.Input
          placeholder={placeholder}
          bg='chakra-body-bg'
          _dark={{ bg: "gray.900" }}
          value={multiple ? inputValue : inputValue || selectedLabel}
        />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      {multiple && (
        <Wrap gap='2' mt={2}>
          {(Array.isArray(value) ? value : []).map((cat) => {
            // Find the subcategory in all categories
            const item = categories
              .flatMap((cat) => cat.subcategories)
              .find((i) => i.value === cat);
            return (
              <Badge key={cat} colorScheme='blue'>
                {item ? item.label : cat}
              </Badge>
            );
          })}
        </Wrap>
      )}
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content style={{ zIndex: 11000 }}>
            <Combobox.Empty>No categories found</Combobox.Empty>
            {categories.map((cat) => (
              <Combobox.ItemGroup key={cat.value}>
                <Combobox.ItemGroupLabel>{cat.label}</Combobox.ItemGroupLabel>
                {cat.subcategories
                  .filter(
                    (sub) =>
                      !inputValue ||
                      sub.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                  )
                  .map((sub) => (
                    <Combobox.Item item={sub} key={sub.value}>
                      {sub.label}
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
              </Combobox.ItemGroup>
            ))}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};
